import * as React from 'react';
import { TTypedTFunction, useTranslation } from '@lib/useTypedTranslation';
import { Link } from 'react-router-dom';
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

import Card from '../../../components/card/card';
import { AmChart } from '../../../components/chart/amChart';
import { coreTheme } from '../../../components/chart/amChart';
import { NoSelectionOverlay } from '../../../components/card/noSelectionOverlay';
import { addHoverAnimation } from '../../../components/chart/onHoverAnimation';
import { useLanguageAndLocaleContext } from '../../../context/languageAndLocale';
import { roundTo2PlacesFixed } from '../../../lib/dataConversion';

import { AxisItemLocation } from '@amcharts/amcharts4/.internal/charts/axes/Axis';
import { isEmpty, isNil } from 'lodash';
import { OverviewCategory } from './common';

import './style.css';

export interface IOverviewCount {
  red: number,
  green: number,
  all: number,
  yellow?: number,
  orange?: number
}

export interface ICategory {
  id: string,
  name: OverviewCategory,
  colour: string,
  path: string
}

interface IProps {
  link: string,
  data: IOverviewCount,
  message: JSX.Element,
  categories?: ICategory[],
  isLoading?: boolean
}

export function isPathDevices(path: string) {
  return path.match(/\/core\/batteries\/devices/);
}

const LABEL_KEYS = {
  battery: {
    problem: 'PROBLEM_BATTERIES',
    warning: 'WARNING_BATTERIES',
    good: 'GOOD_BATTERIES'
  },
  devices: {
    problem: 'PROBLEM_DEVICES',
    warning: 'WARNING_DEVICES',
    good: 'GOOD_DEVICES'
  }
} as const;

export function createTooltip(chartType: string, key: OverviewCategory, t: TTypedTFunction, locale: string) {
  const ns = chartType === 'batteries' ? 'batteryOverview' : 'estateOverview';
  const label = chartType === 'batteries' ? LABEL_KEYS.battery[key] : LABEL_KEYS.devices[key];
  return (_: any, target: any) => {
    const data: any = target.tooltipDataItem.dataContext;
    const totalField = key === 'problem' ? 'totalProblem' : (key === 'warning' ? 'totalWarning' : 'totalGood');
    return `
  <span class="overview-tooltip-text overview-tooltip-text--${key}">
    <div class="key-${key}"></div>
    <span class="label">${t(label as keyof TTypedTFunction, { ns })}: </span>
    <span class="counts">{valueX}% (${data[totalField]?.toLocaleString(locale)} ${t(chartType === 'batteries' ? 'BATTERY_FIRST_CAPITAL' : 'DEVICE', { count: data[totalField], ns: 'translation' })})</span>
  </span>
`;
  };
}

const ns = 'translation' as const;
const ns1 = 'batteryOverview' as const;
const ns2 = 'estateOverview' as const;

export function OverviewAlertsChart(props: IProps) {
  const { t } = useTranslation([ns, ns1, ns2]);
  const { link } = props;
  const { locale } = useLanguageAndLocaleContext();
  const chartType = isPathDevices(link) ? 'device' : 'batteries' as const;

  return (
    <div className="horizontal-chart-container">
      {<NoSelectionOverlay noSelectionText={t(`NO_DATA_AVAILABLE`, { ns: ns })} show={!props.data && !props.isLoading} />}
      <Card>
        <div className="details-message">
          {props.message}
        </div>
        <div className="chart-and-legend-container">
          {props.data && !isEmpty(props.data) && <ChartLegend />}
          <div className="chart-container">
            <AmChart tag='batteryOverview-chart'
              chartProvider={createChart}
              dataProvider={getChartData}
              link={props.data}
              cypressId={`batteryOverviewAlertsChart${chartType}`}
            />
          </div>
        </div>
      </Card>
    </div>
  );

  /* istanbul ignore next */
  function createChart(this: any, id: string) {
    am4core.useTheme(coreTheme);

    const chart = am4core.create(id, am4charts.XYChart);

    const yAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    yAxis.dataFields.category = "title";
    yAxis.renderer.labels.template.disabled = true;

    const xAxis = chart.xAxes.push(new am4charts.ValueAxis());
    xAxis.max = 100;
    xAxis.min = 0;
    xAxis.renderer.baseGrid.disabled = true;
    xAxis.renderer.grid.template.location = 100 as AxisItemLocation;
    xAxis.renderer.grid.template.disabled = true;
    xAxis.renderer.labels.template.disabled = true;

    yAxis.renderer.baseGrid.disabled = false;
    yAxis.renderer.baseGrid.dy = 79;
    yAxis.renderer.baseGrid.strokeWidth = 2;
    yAxis.renderer.baseGrid.stroke = am4core.color("#C2D1E0");
    yAxis.renderer.grid.template.location = 100 as AxisItemLocation;

    function createSeries(name: OverviewCategory, colour: string, link: string) {
      const series = chart.series.push(new am4charts.ColumnSeries());
      series.name = name;
      series.dataFields.valueX = name;
      series.dataFields.categoryY = "title";
      series.sequencedInterpolation = true;
      series.stacked = true;
      series.columns.template.height = am4core.percent(60);
      series.columns.template.stroke = am4core.color('#FFFFFF');
      series.columns.template.fill = am4core.color(colour);
      series.columns.template.tooltipHTML = ``;
      series.columns.template.url = link;
      series.tooltip.pointerOrientation = 'down';
      addHoverAnimation(series, chart);
      series.columns.template.adapter.add('tooltipHTML', createTooltip(chartType, name, t, locale));

      if (name === OverviewCategory.Problem) {
        series.columns.template.column.cornerRadiusTopLeft = 3;
        series.columns.template.column.cornerRadiusBottomLeft = 3;
        series.columns.template.column.adapter.add('cornerRadiusTopRight', (radius, target) => {
          return target.dataItem && parseFloat((target.dataItem.dataContext as any).good) === 0 && parseFloat((target.dataItem.dataContext as any).warning) === 0 ? 3 : radius;
        });
        series.columns.template.column.adapter.add('cornerRadiusBottomRight', (radius, target) => {
          return target.dataItem && parseFloat((target.dataItem.dataContext as any).good) === 0 && parseFloat((target.dataItem.dataContext as any).warning) === 0 ? 3 : radius;
        });
      }

      if (name === OverviewCategory.Warning) {
        series.columns.template.column.adapter.add('cornerRadiusTopRight', (radius, target) => {
          const r = target.dataItem && parseFloat((target.dataItem.dataContext as any).good) === 0 ? 3 : radius;
          return r;
        });
        series.columns.template.column.adapter.add('cornerRadiusBottomRight', (radius, target) => {
          return target.dataItem && parseFloat((target.dataItem.dataContext as any).good) === 0 ? 3 : radius;
        });
        series.columns.template.column.adapter.add('cornerRadiusBottomLeft', (radius, target) => {
          return target.dataItem && parseFloat((target.dataItem.dataContext as any).problem) === 0 ? 3 : radius;
        });
        series.columns.template.column.adapter.add('cornerRadiusTopLeft', (radius, target) => {
          return target.dataItem && parseFloat((target.dataItem.dataContext as any).problem) === 0 ? 3 : radius;
        });
      }

      if (name === OverviewCategory.Good) {
        series.columns.template.column.cornerRadiusTopRight = 3;
        series.columns.template.column.cornerRadiusBottomRight = 3;
        series.columns.template.column.adapter.add('cornerRadiusTopLeft', (radius, target) => {
          return target.dataItem && parseFloat((target.dataItem.dataContext as any).warning) === 0 && parseFloat((target.dataItem.dataContext as any).problem) === 0 ? 3 : radius;
        });
        series.columns.template.column.adapter.add('cornerRadiusBottomLeft', (radius, target) => {
          return target.dataItem && parseFloat((target.dataItem.dataContext as any).warning) === 0 && parseFloat((target.dataItem.dataContext as any).problem) === 0 ? 3 : radius;
        });
      }
    }

    props.categories?.forEach((category) => {
      createSeries(category.name, category.colour, category.path);
    });

    function createRange(value: number) {
      const range = xAxis.axisRanges.create();
      range.value = value;
      range.label.text = "{value}%";
      range.grid.disabled = true;
      range.tick.dy = -10;
      range.tick.stroke = am4core.color("#C2D1E0");
      range.tick.disabled = false;
      range.tick.strokeOpacity = 1;
      range.tick.strokeWidth = 2;
      range.tick.length = 10;
    }

    createRange(0);
    createRange(25);
    createRange(50);
    createRange(75);
    createRange(100);

    return chart;
  }

  function getChartData() {
    if (props.data && !isEmpty(props.data)) {
      const result = [{
        title: 'batteryOverviewAlerts',
        problem: calculatePercentage(props.data.red),
        warning: isNil(props.data?.orange) ? calculatePercentage(props.data.yellow) : calculatePercentage(props.data.orange),
        good: calculatePercentage(props.data.green),
        totalProblem: props.data.red,
        totalWarning: isNil(props.data?.orange) ? props.data.yellow : props.data.orange,
        totalGood: props.data.green
      }];
      return result;
    }
  }

  function calculatePercentage(value: number) {
    return roundTo2PlacesFixed((value / props.data.all) * 100);
  }

  function displayCount(count: number, status: string) {
    // The list containers behind `link` only understand red/yellow/green status query values,
    // so orange (used for the batteries chart's visual warning state) normalises to yellow here.
    const normalizedStatus = status === 'orange' ? 'yellow' : status;
    const total = count === 0 ? 0 : <Link to={`${link}?status=${normalizedStatus}`} data-id={`count-${status}`}>{count.toLocaleString(locale)}</Link>;
    return <span className="total">{total}</span>;
  }

  function ChartLegend() {
    const data = getChartData();
    const problemIcon = <i className="fa fa-times-circle circle_red" role="img" aria-label="problemIcon"/>;
    const warningIcon = <i className="fa fa-exclamation-triangle circle_orange enhanced-warning-icon" role="img" aria-label="warningIcon"/>;
    const goodIcon = <i className="fa fa-check circle_green" role="img" aria-label="goodIcon" />;
    return (
      <div className="overview-chart-legend">
        <span className="problem">
          {problemIcon} {t('PROBLEM', { ns: 'translation' })}:
          {displayCount(data[0].totalProblem, "red")}
        </span>
        <span className="warning">
          {warningIcon} {t('WARNING', { ns: 'translation' })}:
          {displayCount(data[0].totalWarning, isNil(props.data?.orange) ? "yellow" : "orange")}
        </span>
        <span className="good">
          {goodIcon} {t('GOOD', { ns: 'translation' })}:
          {displayCount(data[0].totalGood, "green")}
        </span>
      </div>
    );
  }
}
